import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';
import { AIMarkdownResponse } from '../../../web/components-v2/ai/AiMarkdownResponse';
import type { CoachMessage } from '../ai';
import { IconMaximize, IconMinimize, IconX } from './icons';

/**
 * A single message pinned to the right rail.
 *
 * The left border is colored by the triggering keyword pin (`msg.pinColor`)
 * or falls back to `--accent` for user-pinned coaching messages. A small mono
 * eyebrow surfaces the triggering keyword (uppercased) or "Pinned" when none
 * is set.
 *
 * Body preference order:
 *   1. `msg.sayThis` — the parsed "Say this:" suggestion from the coaching response.
 *   2. `msg.text` — the raw AI text.
 *
 * The card fills whatever cell `PinGrid` gives it and scrolls its body internally. `elevated` marks
 * the Game Plan so it outranks the pins beside it; `live` marks the streaming coaching suggestion.
 */
type PinnedCardProps = {
	msg: CoachMessage;
	onUnpin: () => void;
	/**
	 * When true, render with elevated styling: thicker left border, stronger shadow, slightly larger
	 * eyebrow. Used by the Game Plan tile so it visually outranks the ad-hoc pins beside it.
	 */
	elevated?: boolean;
	/** The live coaching suggestion — adds a LIVE badge and the websocket indicator. */
	live?: boolean;
	/** True while that live suggestion is still streaming in. */
	streaming?: boolean;
	/** Realtime-coaching socket state; only meaningful on the live tile. */
	wsConnected?: boolean;
	maximized?: boolean;
	/** Fill the whole grid with this tile for a long read, or restore it. */
	onToggleMaximize?: () => void;
};

export const PinnedCard: FC<PinnedCardProps> = ({
	msg,
	onUnpin,
	elevated,
	live,
	streaming,
	wsConnected,
	maximized,
	onToggleMaximize,
}) => {
	const color = msg.pinColor || 'var(--accent)';
	const eyebrow = (msg.pinnedKeyword || 'Pinned').toUpperCase();
	const body = msg.sayThis?.trim() || msg.text || '';

	// The grid owns tile height now, so the card fills its cell and scrolls internally. The old
	// drag-to-resize handle (and its persisted heights) is gone: with a grid there is no free space for
	// one card to claim, so maximize is the affordance for a long read.
	const cardStyle: React.CSSProperties = {
		borderLeftColor: color,
		...(elevated
			? {
					borderLeftWidth: 5,
					boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
				}
			: {}),
	};

	return (
		<div
			className={css(styles.card, live && styles.cardLive)}
			style={cardStyle}
			data-elevated={elevated ? 'true' : 'false'}
			data-live={live ? 'true' : 'false'}
		>
			<div className={css(styles.header)}>
				<span className={css(styles.eyebrow, elevated && styles.eyebrowElevated)} style={{ color }}>
					{eyebrow}
				</span>
				{live && <span className={css(styles.liveBadge)}>LIVE</span>}
				{live && streaming && <span className={css(styles.streamDot)} aria-label='Streaming' />}
				{live && wsConnected !== undefined && (
					<span
						className={css(styles.wsDot, wsConnected ? styles.wsDotOn : styles.wsDotOff)}
						title={wsConnected ? 'Realtime coaching connected' : 'Realtime coaching disconnected'}
						aria-label={wsConnected ? 'Realtime coaching connected' : 'Realtime coaching disconnected'}
						data-testid='ws-status-dot'
					/>
				)}
				<span className={css(styles.spacer)} />
				{onToggleMaximize && (
					<button
						type='button'
						title={maximized ? 'Restore' : 'Maximize'}
						aria-label={maximized ? 'Restore pinned message' : 'Maximize pinned message'}
						onClick={onToggleMaximize}
						className={css(styles.closeBtn)}
					>
						{maximized ? <IconMinimize size={12} /> : <IconMaximize size={12} />}
					</button>
				)}
				<button type='button' title='Unpin' aria-label='Unpin' onClick={onUnpin} className={css(styles.closeBtn)}>
					<IconX size={12} />
				</button>
			</div>
			<div className={css(styles.body)}>
				<AIMarkdownResponse>{body}</AIMarkdownResponse>
			</div>
			{msg.time && <div className={css(styles.time)}>{msg.time}</div>}
		</div>
	);
};

const styles = StyleSheet.create({
	card: {
		position: 'relative',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		background: 'var(--bg-elev)',
		borderRadius: 10,
		border: '1px solid var(--border)',
		borderLeft: '3px solid var(--accent)',
		padding: '12px 14px',
		boxShadow: 'var(--shadow-sm)',
		minHeight: 0,
		minWidth: 0,
		overflow: 'hidden',
	},
	cardLive: {
		borderColor: 'var(--accent)',
		boxShadow: '0 0 0 3px var(--ring), var(--shadow-md)',
	},
	liveBadge: {
		fontFamily: 'var(--font-mono)',
		fontSize: 8.5,
		fontWeight: 600,
		letterSpacing: '0.1em',
		color: 'var(--bg-elev)',
		background: 'var(--accent)',
		padding: '2px 6px',
		borderRadius: 4,
	},
	streamDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		background: 'var(--accent)',
	},
	wsDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
	},
	wsDotOn: {
		background: 'var(--success)',
	},
	wsDotOff: {
		background: 'var(--danger)',
	},
	spacer: {
		flex: 1,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		marginBottom: 6,
		flexShrink: 0,
	},
	eyebrow: {
		fontFamily: 'var(--font-mono)',
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
	},
	eyebrowElevated: {
		fontSize: 11.5,
	},
	closeBtn: {
		width: 22,
		height: 22,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 6,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	body: {
		fontSize: 12.5,
		color: 'var(--ink)',
		lineHeight: 1.5,
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
	},
	time: {
		marginTop: 8,
		fontSize: 10,
		color: 'var(--ink-faint)',
		fontFamily: 'var(--font-mono)',
		flexShrink: 0,
	},
});
